import { useMemo, useState } from "react";
import { Check, Copy, ChevronsUpDown } from "lucide-react";
import t from "@/lang/lang";
import { Button } from "@/shared/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/shared/components/ui/command";
import { cn } from "@/shared/lib/utils";
import {
  addressListLabel,
  sameAddress,
  type Address,
} from "@/services/backend/addresses/types/address";
import { useBuildingsByAddressListQuery } from "@/services/backend/buildings/api/building-by-address-api";

export function BuildingAddressSelect({
  customerId,
  currentAddress,
  onAddressSelect,
}: {
  customerId: string;
  currentAddress: Address;
  onAddressSelect: (address: Address) => void;
}) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useBuildingsByAddressListQuery(
    { customer: { active: true, values: [customerId] } },
    { skip: !customerId },
  );
  const groups = useMemo(() => data?.items ?? [], [data?.items]);
  const currentKey = groups.find((group) =>
    sameAddress(group.address, currentAddress),
  )?.addressKey;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="max-w-[24rem] justify-between sm:w-[24rem]"
        >
          <span className="flex min-w-0 items-center gap-2 truncate">
            <Copy className="h-4 w-4 shrink-0" />
            <span className="truncate">
              {currentKey
                ? addressListLabel(currentAddress)
                : t("Adresse kopieren")}
            </span>
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[min(42rem,calc(100vw-2rem))] p-0"
        align="start"
      >
        <Command>
          <CommandInput placeholder={t("Adresse suchen")} className="h-9" />
          <CommandList>
            <CommandEmpty>
              {isLoading ? t("Lade Daten ...") : t("Keine Adresse gefunden")}
            </CommandEmpty>
            <CommandGroup>
              {groups.map((group) => (
                <CommandItem
                  key={group.addressKey}
                  value={addressListLabel(group.address)}
                  onSelect={() => {
                    onAddressSelect(group.address);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      currentKey === group.addressKey
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  <div className="min-w-0">
                    <div className="break-words">
                      {addressListLabel(group.address)}
                    </div>
                    <div className="text-muted-foreground text-xs">
                      {group.buildings.length} {t("Gebäude")}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
